import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useLanguageOptional } from '@/components/language-context'
import { Image } from '@/components/ui/image'
import type {
  ClassExample,
  Example,
  FunctionExample,
  RawExample,
} from './problem-context'
import {
  isClassExample,
  isFunctionExample,
  isRawExample,
} from './problem-context'

interface ExampleCardProps {
  example: Example
}

/**
 * Renders a single `name = value` pair, detecting if it's multiline
 * and rendering appropriately. Used for inputs and for the after-state
 * of in-place params in the Output section.
 */
function NamedValue({ name, value }: { name: string; value: string }) {
  const isMultiline = value.includes('\n')

  if (isMultiline) {
    return (
      <pre className="overflow-x-auto p-2 font-mono text-xs">
        {name} = {value.trim()}
      </pre>
    )
  }

  return (
    <div>
      <code className="font-mono text-xs">
        {name} = {value}
      </code>
    </div>
  )
}

/**
 * Renders the output section.
 *
 * For in-place/void problems the answer lives in the after-state of the
 * mutated params rather than in a return value, so both are shown when both
 * exist — hiding either would misrepresent what the function does. Layout
 * mirrors InputSection: inline for a single short value, block otherwise.
 */
function OutputSection({
  output,
  inputsAfter,
}: {
  output: string
  inputsAfter?: Record<string, string>
}) {
  // Destructured rather than length-checked so the non-empty precondition
  // OutputWithAfterState relies on travels in the type, not in this guard.
  const [firstAfter, ...restAfter] = Object.entries(inputsAfter ?? {})

  if (firstAfter) {
    return (
      <OutputWithAfterState
        output={output}
        afterEntries={[firstAfter, ...restAfter]}
      />
    )
  }

  const isMultiline = output.includes('\n')

  if (isMultiline) {
    return (
      <div className="mt-3 text-gray-700 dark:text-gray-300">
        <div className="font-medium text-gray-900 dark:text-gray-100">
          Output:
        </div>
        <div className="mt-1 pl-4">
          <pre className="overflow-x-auto p-2 font-mono text-xs">
            {output.trim()}
          </pre>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 text-gray-700 dark:text-gray-300">
      <span className="font-medium text-gray-900 dark:text-gray-100">
        Output:
      </span>{' '}
      <code className="font-mono text-xs">{output}</code>
    </div>
  )
}

/**
 * Output for a call whose result (or part of it) is a mutated param.
 * Single short after-state and no return value reads as one line
 * (`Output: arr = [3,2,1]`); anything longer gets the block layout.
 *
 * A return value always renders, even when it repeats an after-state value
 * (an in-place problem returning the mutated param prints the same answer
 * twice, once unlabeled). The brief's rule is "show both", and every attempt
 * to dedupe the repeat had to decide whether two JSON texts mean the same
 * value — a compare with enough sharp edges (number-lexeme collapse, key
 * order, non-JSON text) that a coincidental match could hide a genuine
 * return.
 */
function OutputWithAfterState({
  output,
  afterEntries,
}: {
  output: string
  afterEntries: [[string, string], ...Array<[string, string]>]
}) {
  const hasReturnValue = output.trim().length > 0
  const [firstName, firstValue] = afterEntries[0]

  if (
    !hasReturnValue &&
    afterEntries.length === 1 &&
    !firstValue.includes('\n')
  ) {
    return (
      <div className="mt-3 text-gray-700 dark:text-gray-300">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          Output:
        </span>{' '}
        <code className="font-mono text-xs">
          {firstName} = {firstValue}
        </code>
      </div>
    )
  }

  return (
    <div className="mt-3 text-gray-700 dark:text-gray-300">
      <div className="font-medium text-gray-900 dark:text-gray-100">
        Output:
      </div>
      <div className="mt-1 space-y-1 pl-4">
        {hasReturnValue &&
          (output.includes('\n') ? (
            <pre className="overflow-x-auto p-2 font-mono text-xs">
              {output.trim()}
            </pre>
          ) : (
            <div>
              <code className="font-mono text-xs">{output}</code>
            </div>
          ))}
        {afterEntries.map(([name, value]) => (
          <NamedValue key={name} name={name} value={value} />
        ))}
      </div>
    </div>
  )
}

/**
 * Renders the input section based on the number of inputs.
 * - Single input: inline with "Input:" label
 * - Multiple inputs: each on its own line below the label
 */
function InputSection({ inputs }: { inputs: Record<string, string> }) {
  if (!inputs) return null
  const entries = Object.entries(inputs)

  if (entries.length === 1) {
    const [key, value] = entries[0]
    const isMultiline = value.includes('\n')

    if (isMultiline) {
      return (
        <div className="text-gray-700 dark:text-gray-300">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Input:
          </span>
          <div className="mt-1 pl-4">
            <NamedValue name={key} value={value} />
          </div>
        </div>
      )
    }

    return (
      <div className="text-gray-700 dark:text-gray-300">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          Input:
        </span>{' '}
        <code className="font-mono text-xs">
          {key} = {value}
        </code>
      </div>
    )
  }

  return (
    <div className="text-gray-700 dark:text-gray-300">
      <div className="font-medium text-gray-900 dark:text-gray-100">
        Inputs:
      </div>
      <div className="mt-1 space-y-1 pl-4">
        {entries.map(([key, value]) => (
          <NamedValue key={key} name={key} value={value} />
        ))}
      </div>
    </div>
  )
}

/**
 * Renders a class-based example using the input display override
 * Class-based problems show the sequence of operations via inputDisplayOverride
 */
function ClassExampleSection({ example }: { example: ClassExample }) {
  if (!example.inputDisplayOverride) {
    return (
      <div className="text-muted-foreground text-sm">
        No example display available.
      </div>
    )
  }

  return (
    <pre className="overflow-x-auto rounded bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
      {example.inputDisplayOverride.trim()}
    </pre>
  )
}

/**
 * Renders a raw example as a formatted code block.
 * Shows the test script for the current language if available,
 * otherwise falls back to the exampleDisplay text.
 */
function RawExampleSection({ example }: { example: RawExample }) {
  const langCtx = useLanguageOptional()
  const uiLang = langCtx?.language ?? 'python'
  const scriptKey = uiLang === 'js' ? 'javascript' : uiLang
  const scriptCode = example.scripts?.[scriptKey]
  const displayText = scriptCode?.trim() ? scriptCode : example.display

  if (!displayText.trim()) return null

  return (
    <pre className="overflow-x-auto rounded bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
      {displayText.trim()}
    </pre>
  )
}

/**
 * Renders a function-based example with inputs and output
 */
function FunctionExampleSection({ example }: { example: FunctionExample }) {
  return (
    <div className="space-y-1 text-sm">
      {/* Input section - use override if available, skip if no inputs */}
      {example.inputDisplayOverride ? (
        <div className="text-gray-700 dark:text-gray-300">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Input:
          </span>
          <pre className="mt-1 overflow-x-auto p-2 font-mono text-xs">
            {example.inputDisplayOverride}
          </pre>
        </div>
      ) : example.inputs && Object.keys(example.inputs).length > 0 ? (
        <InputSection inputs={example.inputs} />
      ) : (
        <div className="text-gray-700 dark:text-gray-300">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Input:
          </span>{' '}
          <span className="text-muted-foreground italic">(none)</span>
        </div>
      )}

      {/* Output line - use override if available */}
      {example.outputDisplayOverride ? (
        <div className="mt-3 text-gray-700 dark:text-gray-300">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Output:
          </span>
          <pre className="mt-1 overflow-x-auto p-2 font-mono text-xs">
            {example.outputDisplayOverride}
          </pre>
        </div>
      ) : (
        <OutputSection
          output={example.output}
          inputsAfter={example.expectedInputsAfter}
        />
      )}
    </div>
  )
}

/**
 * ExampleCard renders a single example in LeetCode-style formatting
 * Supports both function-based and class-based examples
 */
export function ExampleCard({ example }: ExampleCardProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div className="example-card mb-4 rounded-lg border border-gray-200 p-4 dark:border-neutral-700">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="mb-2 flex w-full items-center gap-2 text-left font-medium text-gray-900 dark:text-gray-100 hover:opacity-80"
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        )}
        Example {example.index}:
      </button>

      {isExpanded && (
        <>
          {'imageFilename' in example && example.imageFilename && (
            <div className="mb-3">
              <Image
                src={`images/${example.imageFilename}`}
                alt={`Example ${example.index} diagram`}
                className="max-h-64 w-auto"
                srcSet={false}
              />
            </div>
          )}
          {isRawExample(example) ? (
            <RawExampleSection example={example} />
          ) : isClassExample(example) ? (
            <ClassExampleSection example={example} />
          ) : isFunctionExample(example) ? (
            <FunctionExampleSection example={example} />
          ) : (
            <div className="text-muted-foreground text-sm">
              No example data available.
            </div>
          )}
        </>
      )}
    </div>
  )
}
